import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import {
	Button, DatePicker, Empty, Form, Input, Popconfirm, Row, Table, Tag, Tooltip
} from 'antd'
import { DeleteOutlined, PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/es/table'
import {
	NumberParam, NumericArrayParam, StringParam, useQueryParams, withDefault
} from 'use-query-params'
import { useHistory } from 'react-router-dom'
import {
	COMPLAINT_STATUS, deleteComplaint, getComplaints, IComplaintQueryParams, IComplaintTableItem, translateComplaintStatus, translateComplaintStatusColor
} from '../../reducers/complaints/actions'

import { RootState } from '../../reducers'
import SelectJobsField from '../../components/SelectJobsField'
import SelectUsersField from '../../components/SelectUsersField'
import { USER_ROLE } from '../../utils/enums'

const { RangePicker } = DatePicker
const PAGE_SIZE = 100

interface ISelectOption {
	value: number
	label: string
}

interface ISelectChange {
	value: ISelectOption[]
}

const ComplaintsList = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { t } = useTranslation('pages')

	const dataSource = useSelector((state: RootState) => state.complaints.complaints)
	const isLoadingList = dataSource.isLoading

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		limit: withDefault(NumberParam, PAGE_SIZE),
		users: withDefault(NumericArrayParam, []),
		jobs: withDefault(NumericArrayParam, []),
		page: withDefault(NumberParam, 1),
		startDate: StringParam,
		endDate: StringParam
	})

	const [dateRange, setDateRange] = useState<any>([query.startDate ? dayjs(query.startDate) : null, query.endDate ? dayjs(query.endDate) : null])

	useEffect(() => {
		const cleanedQuery = {
			...query,
			users: query.users?.filter((u): u is number => typeof u === 'number'),
			jobs: query.jobs?.filter((j): j is number => typeof j === 'number')
		} as IComplaintQueryParams

		dispatch(getComplaints(cleanedQuery))
	}, [dispatch, query])

	const handleDelete = (id: number) => {
		const cleanedQuery = {
			...query,
			users: query.users?.filter((u): u is number => typeof u === 'number'),
			jobs: query.jobs?.filter((j): j is number => typeof j === 'number')
		} as IComplaintQueryParams
		dispatch(deleteComplaint(id, () => dispatch(getComplaints(cleanedQuery))))
	}

	const columns: ColumnsType<IComplaintTableItem> = [
		{
			title: t('pages:Zákazka'),
			dataIndex: 'jobAddress',
			key: 'jobAddress',
			ellipsis: true,
			render: (value) => value || '-'
		},
		{
			title: t('pages:Zamestnanec'),
			dataIndex: 'cleanerName',
			key: 'cleanerName',
			ellipsis: true,
			render: (value) => value || '-'
		},
		{
			title: t('pages:Stav'),
			dataIndex: 'status',
			key: 'status',
			ellipsis: true,
			// eslint-disable-next-line react/display-name
			render: (value: COMPLAINT_STATUS) => (
				<Tag color={translateComplaintStatusColor(value)}>
					{translateComplaintStatus(value, t)}
				</Tag>
			)
		},
		{
			title: t('pages:Poznámka'),
			dataIndex: 'note',
			key: 'note',
			ellipsis: true,
			render: (value) => value || '-'
		},
		{
			title: t('pages:Dátum'),
			dataIndex: 'createdAt',
			key: 'createdAt',
			ellipsis: true,
			render: (value) => value
		},
		{
			title: '',
			key: 'operation',
			width: 80,
			fixed: 'right',
			render(_, record) {
				return (
					<Popconfirm
						title={t('pages:Skutočne chcete vymazať záznam?')}
						icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
						onConfirm={(e) => {
							e?.stopPropagation()
							handleDelete(record.id)
						}}
						onCancel={(e) => e?.stopPropagation()}
						okText={t('pages:Vymazať')}
						cancelText={t('pages:Zrušiť')}
					>
						<Button icon={<DeleteOutlined />} type={'primary'} onClick={(e) => e.stopPropagation()} />
					</Popconfirm>
				)
			}
		}
	]

	const handleJobsChange = (data: ISelectChange) => {
		setQuery({
			...query,
			jobs: data.value.map((item) => item.value)
		})
	}

	const handleUsersChange = (data: ISelectChange) => {
		setQuery({
			...query,
			users: data.value.map((item) => item.value)
		})
	}

	const handleDateRangeChange = (dates: any) => {
		setDateRange(dates)
		setQuery({
			...query,
			startDate: dates?.[0] ? dates[0].startOf('day').toISOString() : undefined,
			endDate: dates?.[1] ? dates[1].endOf('day').toISOString() : undefined,
			page: 1
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({
		...query,
		search: searchTerm
	}), 300), [query])

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => debounced(e.target.value)

	return (
		<div className={'page-wrapper'}>
			<div className={'flex justify-between'}>
				<Form.Item>
					<Input.Search onChange={handleOnChange} style={{ width: 300 }} allowClear/>
				</Form.Item>
				<Row>
					<Form.Item>
						<RangePicker
							value={dateRange}
							onChange={handleDateRangeChange}
						/>
					</Form.Item>
					<Button
						type={'primary'}
						icon={<PlusCircleOutlined />}
						className={'ml-4'}
						href={t('paths:complaintCreate|path')}
					>
						{t('pages:Vytvoriť sťažnosť')}
					</Button>
				</Row>
			</div>

			<div className={'flex'} style={{ flexWrap: 'wrap' }}>
				<Form.Item className={'mr-2'}>
					<SelectUsersField
						onValueChange={handleUsersChange}
						userValues={query.users}
						multiple={true}
						queryParams={{ role: [USER_ROLE.USER, USER_ROLE.ADMIN, USER_ROLE.SUPERADMIN] }}
					/>
				</Form.Item>
				<Form.Item>
					<SelectJobsField
						onValueChange={handleJobsChange}
						jobValues={query.jobs}
						usersFilter={query.users}
					/>
				</Form.Item>
			</div>

			<Table
				className={'general-table'}
				columns={columns}
				dataSource={dataSource.tableData}
				style={{ marginTop: 0 }}
				loading={isLoadingList}
				pagination={{
					pageSize: PAGE_SIZE,
					total: dataSource.pagination?.totalCount,
					current: dataSource.pagination?.page,
					showSizeChanger: false
				}}
				onRow={(record) => ({
					onClick: () => history.push(`${t('paths:complaintDetail|path')}/${record.id}`)
				})}
				locale={{ emptyText: <Empty description={t('pages:Žiadne reklamácie')}/> }}
				size={'small'}
			/>
		</div>
	)
}

export default ComplaintsList
